import React, { createContext, useState, useContext, useEffect, lazy } from 'react';
import Header from './../Header';
import Home from './../pages/Home';
import Form from './../pages/Form';
import Results from './../pages/Results';

import HeaderNew from './../HeaderNew';
import HomeNew from './../pages/HomeNew';
import FormNew from './../pages/FormNew';
import ResultsNew from './../pages/ResultsNew';
import Products from './../pages/Products';

import MobileVariant from '@/pages/MobileVariant';

interface VariantContextValue {
  variant: string;
  setVariant: React.Dispatch<React.SetStateAction<string>>;
  isNewVariant: boolean;
  getQuestionVariant: (type: string) => React.ComponentType<any>|null
  getVariantElements: () => VariantElements
}

export interface VariantElements {
  name: string,
  loadCss?: () => Promise<unknown>,
  headerElement: React.ComponentType,
  homeElement: React.ComponentType,
  formElement: React.ComponentType,
  resultsElement: React.ComponentType,
  productsElement?: React.ComponentType,

  question_checkbox: React.ComponentType<any>,
  question_dropdown: React.ComponentType<any>,
  "question_checkbox-image": React.ComponentType<any>,
  question_image: React.ComponentType<any>,
  question_switch: React.ComponentType<any>,
}

const EMPTY_VARIANT: VariantElements = {
  name: "Unset",
  headerElement: () => {},
  homeElement: () => {},
  formElement: () => {},
  resultsElement: () => {},

  question_checkbox: () => {},
  "question_checkbox-image": () => {},
  question_dropdown: () => {},
  question_image: () => {},
  question_switch: () => {},
};

export const VariantContext = createContext<VariantContextValue>({
  variant: 'default',
  setVariant: () => {},
  isNewVariant: false,
  getQuestionVariant: (t: string) => null,
  getVariantElements: () => EMPTY_VARIANT,
});

export const VariantProvider = ({ children }: { children: React.ReactNode }) => {
  const [variant, setVariant] = useState<string>(
    import.meta.env.VITE_PLATFORM_VARIANT || 'default',
  );

  const isNewVariant = variant === 'crosscare';

  const questionTypeMap: {[key: string]: { [k in keyof VariantElements]: VariantElements[k] extends React.ComponentType<any> ? k : never }[keyof VariantElements]} = {
    "checkbox": "question_checkbox",
    "checkbox-image": "question_checkbox-image",
    "dropdown": "question_dropdown",
    "image": "question_image",
    "switch": "question_switch",
  }

  const DEFAULT_COMPONENTS: VariantElements = {
      name: "Default",
      headerElement: Header,
      homeElement: Home,
      formElement: Form,
      resultsElement: Results,

      question_checkbox: React.useMemo(() => lazy(() => import('../components/Checkbox')), []),
      "question_checkbox-image": React.useMemo(() => lazy(() => import('../components/CheckboxImage')), []),
      question_dropdown: React.useMemo(() => lazy(() => import('../components/Dropdown')), []),
      question_image:React.useMemo(() => lazy(() => import('../components/Image')), []),
      question_switch: React.useMemo(() => lazy(() => import('../components/Switch')), []),
    
  }
  // TODO: Refactor this to main index file
  const variantComponents: {[key: string]: VariantElements} = {
    "crosscare": {
      ...DEFAULT_COMPONENTS,
      name: "CrossCare",
      loadCss: () => import('../indexNew.css'),
      headerElement: HeaderNew,
      homeElement: HomeNew,
      formElement: FormNew,
      resultsElement: ResultsNew,
      productsElement: Products,

      question_checkbox: React.useMemo(() => lazy(() => import('../components/CheckboxNew')), []),
      "question_checkbox-image": React.useMemo(() => lazy(() => import('../components/CheckboxImageNew')), []),
      question_dropdown: React.useMemo(() => lazy(() => import('../components/DropdownNew')), []),
    },
    "mobile": {...DEFAULT_COMPONENTS, ...MobileVariant},
    "algemeen": DEFAULT_COMPONENTS,
  }

  const getVariantElements = () => {
    if( variantComponents.hasOwnProperty(variant) ) return variantComponents[variant];
    return DEFAULT_COMPONENTS;
  };

  const getQuestionVariant = (type: string) => {
    if( !questionTypeMap.hasOwnProperty(type) ) return null;
    const key = questionTypeMap[type];
    if( key === undefined ) return null;
    return getVariantElements()[key];
  }

  useEffect(() => {
    const components = getVariantElements();
    if (components.loadCss) {
      components.loadCss().catch((err) =>
        console.error('Failed to load variant-specific CSS:', err),
      );
    }
  }, [variant]);

  useEffect(() => {
    const updateVariantFromCustomer = () => {
      if (window.customer && window.customer.variant) {
        const envVariant = import.meta.env.VITE_PLATFORM_VARIANT;
        if (envVariant != null && envVariant.length > 0) {
          if (variant !== envVariant) setVariant(envVariant);
        } else if (variant !== window.customer.variant) {
          setVariant(window.customer.variant);
        }
      }
    };

    if (window.customer) {
      updateVariantFromCustomer();
    } else {
      const checkInterval = setInterval(() => {
        if (window.customer) {
          updateVariantFromCustomer();
          clearInterval(checkInterval);
        }
      }, 100);
      return () => clearInterval(checkInterval);
    }
  }, []);

  return (
    React.createElement(
      VariantContext.Provider,
      { value: { variant, setVariant, isNewVariant, getVariantElements, getQuestionVariant } },
      children,
    )
  );
};

export const useVariant = (): VariantContextValue => {
  const context = useContext(VariantContext);
  if (context === undefined) {
    throw new Error('useVariant must be used within a VariantProvider');
  }
  return context;
};

export default VariantProvider;
